import { useMemo } from 'react';
import { SessionDetail } from '../../types/session-types';
import { BreakDownItem } from '../../types/api-types';
import { MeterValueGridRow } from '../../types/meter-value-types';

interface TariffInfoSectionProps {
  detail: SessionDetail;
  meterValues?: MeterValueGridRow[];
}

interface TariffGroup {
  name: string;
  unitCost: number | null;
  energyKwh: number;
  costExclVat: number;
  vatCost: number;
  costInclVat: number;
}

/** Aggregate breakdown items by tariff name */
function aggregateBreakdowns(items: BreakDownItem[]): TariffGroup[] {
  const map = new Map<string, TariffGroup>();
  for (const item of items) {
    const key = item.taarifName ?? 'Unknown';
    if (!map.has(key)) {
      map.set(key, { name: key, unitCost: item.unitPrice || null, energyKwh: 0, costExclVat: 0, vatCost: 0, costInclVat: 0 });
    }
    const g = map.get(key)!;
    g.energyKwh += (item.energy ?? 0) / 1000; // Wh → kWh
    g.costExclVat += item.cost ?? 0;
    g.vatCost += item.vatCost ?? 0;
    g.costInclVat += item.totalCost ?? 0;
  }
  return Array.from(map.values());
}

/** Aggregate meter values by tariff name (estimation for active sessions) */
function aggregateMeterValues(meterValues: MeterValueGridRow[]): TariffGroup[] {
  const map = new Map<string, TariffGroup>();
  for (const row of meterValues) {
    if (!row.tariffName || row.deltaEnergy == null) continue;
    const key = row.tariffName;
    if (!map.has(key)) {
      map.set(key, { name: key, unitCost: row.unitCost, energyKwh: 0, costExclVat: 0, vatCost: 0, costInclVat: 0 });
    }
    const g = map.get(key)!;
    g.energyKwh += row.deltaEnergy ?? 0;
    g.costExclVat += row.costExclVat ?? 0;
    g.vatCost += row.vatCost ?? 0;
    g.costInclVat += row.costInclVat ?? 0;
  }
  return Array.from(map.values());
}

export function TariffInfoSection({ detail, meterValues = [] }: TariffInfoSectionProps) {
  const isActive = !detail.stopTime;

  // Priority 1: breakdown data from sessionbreakdown API (completed sessions)
  const breakdownGroups = useMemo<TariffGroup[]>(() => {
    if (!detail.breakdownItems?.length) return [];
    return aggregateBreakdowns(detail.breakdownItems);
  }, [detail.breakdownItems]);

  // Priority 2: meter value aggregation (active sessions — estimated)
  const meterValueGroups = useMemo<TariffGroup[]>(() => {
    if (breakdownGroups.length > 0) return []; // defer to breakdown
    return aggregateMeterValues(meterValues);
  }, [breakdownGroups.length, meterValues]);

  const groups = breakdownGroups.length > 0 ? breakdownGroups : meterValueGroups;
  const isEstimated = breakdownGroups.length === 0 && meterValueGroups.length > 0;

  // Totals
  const totalCostExclVat = groups.reduce((s, g) => s + g.costExclVat, 0);
  const totalVat = groups.reduce((s, g) => s + g.vatCost, 0);
  const totalCostInclVat = groups.reduce((s, g) => s + g.costInclVat, 0);

  // Fallback: L1-L9 from session detail (older completed sessions)
  const hasL1L9 = groups.length === 0 && (
    detail.l1UnitCost !== null || detail.l2UnitCost !== null || detail.l3UnitCost !== null
  );

  if (groups.length === 0 && !hasL1L9) {
    return (
      <div className="border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Tariff</h3>
        <p className="text-xs text-gray-400">No tariff data available</p>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Tariff</h3>

      {groups.length > 0 && (
        <>
          {isEstimated && (
            <p className="text-xs text-gray-400 italic mb-4">
              Estimated values based on received meter readings — final amounts may differ upon session completion.
            </p>
          )}

          {/* Per-tariff rows — flat grid matching ChargingInfoSection style */}
          {groups.map((g, i) => (
            <div key={g.name} className={`grid grid-cols-4 gap-4 ${i < groups.length - 1 ? 'mb-4' : ''}`}>
              <InfoItem label="Tariff" value={g.name} />
              <InfoItem label="Unit Cost" value={g.unitCost != null ? g.unitCost.toFixed(4) : '--'} />
              <InfoItem label="Energy (kWh)" value={g.energyKwh.toFixed(3)} />
              <InfoItem label="Cost (excl. VAT)" value={g.costExclVat.toFixed(2)} />
            </div>
          ))}

          {/* Totals row */}
          <div className="border-t border-gray-200 mt-4 pt-4 grid grid-cols-3 gap-4">
            <InfoItem label="Total excl. VAT" value={totalCostExclVat.toFixed(2)} />
            <InfoItem label="VAT" value={totalVat.toFixed(2)} />
            <InfoItem label="Total incl. VAT" value={totalCostInclVat.toFixed(2)} />
          </div>
        </>
      )}

      {/* Fallback: L1-L9 breakdown for older completed sessions */}
      {hasL1L9 && (
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3,4,5,6,7,8,9].map((n) => {
            const unitCost = (detail as unknown as Record<string, unknown>)[`l${n}UnitCost`] as number | null;
            const energy = (detail as unknown as Record<string, unknown>)[`l${n}Energy`] as number | null;
            const cost = (detail as unknown as Record<string, unknown>)[`l${n}Cost`] as number | null;
            if (unitCost == null) return null;
            return (
              <div key={n}>
                <div className="text-xs text-gray-500 mb-1">Level {n}</div>
                <div className="text-sm font-semibold text-gray-900">{unitCost.toFixed(4)}</div>
                <div className="text-xs text-gray-500">Energy: {energy?.toFixed(3) ?? '--'} kWh</div>
                <div className="text-xs text-gray-500">Cost: {cost?.toFixed(2) ?? '--'}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface InfoItemProps {
  label: string;
  value: string;
}

function InfoItem({ label, value }: InfoItemProps) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-gray-900">{value}</div>
    </div>
  );
}
